import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("paypal_order_id");
  const [status, setStatus] = useState("checking");
  const [txnData, setTxnData] = useState(null);
  const polled = useRef(false);

  useEffect(() => {
    if ((!sessionId && !paypalOrderId) || polled.current) return;
    polled.current = true;

    const statusUrl = paypalOrderId
      ? `${API}/paypal/status/${paypalOrderId}`
      : `${API}/payments/status/${sessionId}`;

    const poll = async (attempts = 0) => {
      if (attempts >= 8) { setStatus("timeout"); return; }
      try {
        const res = await axios.get(statusUrl);
        const { payment_status } = res.data;
        if (payment_status === "paid") { setStatus("paid"); setTxnData(res.data); return; }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(attempts + 1), 2500);
      } catch {
        setTimeout(() => poll(attempts + 1), 2500);
      }
    };
    poll();
  }, [sessionId, paypalOrderId]);

  if (status === "checking") return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="payment-checking">
      <div className="text-center">
        <Loader2 size={40} className="text-zinc-500 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-bold text-zinc-900 mb-2">Confirming your payment...</h2>
        <p className="text-zinc-500 text-sm">This usually takes a few seconds.</p>
      </div>
    </div>
  );

  if (status === "paid") return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-success">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-6">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h1 className="text-3xl font-extrabold text-zinc-950 mb-3">Payment Confirmed!</h1>
        <p className="text-zinc-600 mb-2">Your 50% upfront payment was received successfully.</p>
        <p className="text-zinc-500 text-sm mb-8">We'll start working on your MVP immediately and be in touch within 24 hours.</p>
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-5 mb-8 text-left">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">What happens next</div>
          {["Our team reviews your scope of work", "You receive a welcome email within 24 hours", "Development begins immediately", "You receive updates throughout the build", "Final 50% due upon delivery"].map((step, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-100 last:border-0">
              <div className="w-5 h-5 rounded-full bg-zinc-950 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</div>
              <span className="text-sm text-zinc-700">{step}</span>
            </div>
          ))}
        </div>
        <button data-testid="btn-back-home" onClick={() => navigate("/")}
          className="inline-flex items-center px-6 py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
          Back to Homepage <ArrowRight size={15} className="ml-2" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-failed">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-2xl mb-6">
          <XCircle size={32} className="text-red-600" />
        </div>
        <h1 className="text-2xl font-extrabold text-zinc-950 mb-2">Payment Not Confirmed</h1>
        <p className="text-zinc-600 mb-6 text-sm">We couldn't confirm your payment. If you were charged, please contact us at <a href="mailto:hello@fastprototype.ai" className="underline">hello@fastprototype.ai</a></p>
        <button onClick={() => navigate("/build")} className="inline-flex items-center px-6 py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
          Try Again
        </button>
      </div>
    </div>
  );
}
